import React, { useState, useEffect, useRef } from 'react';
import cx from 'classnames';
import Styles from '@pages/analytics/analytics.module.scss';
import DefectsAnalyticTable from '@pages/analytics/common/defectsAnalyticsTable';
import DefectsDonutChart from '@pages/common/charts/defects-donut-chart';
import { chartFontFamily, NO_DATA_FOUND, defectAnalyticsLabel } from '@common/ui-constants';
import { getDefectStatisticTableData, defectsDonutChartCustomTooltip } from '@pages/analytics/common/util';
import { isEmptyValue, scrollTop } from '@src/util/common_utils';
import { colors } from '@src/css_config/colorConstants';

const SeverityStatistics = (props) => {
  let {
    getBugSummary,
    openSeverityBugSummaryModal,
    closeBugSummaryModal,
    selectedDefectID,
    severityChartData,
    reqBody,
    callAllApiOnFilterChange,
  } = props;

  let [series, setSeries] = useState([]);
  let [chartLabel, setChartLabel] = useState([]);
  let [seriesColor, setSeriesColor] = useState([]);
  let [tableData, setTableData] = useState([]);
  let [tableContent, setTableContent] = useState('ALL');
  let [callOnViewAll, setCallOnViewAll] = useState(false);

  //? States for scroll APi :: START
  let [initTable, setInitTable] = useState({
    from: 0,
    to: 20,
  });

  const severityTableRef = useRef();
  const [severityTableScrollPos, setSeverityTableScrollPos] = useState(false);
  const [stopScrollApiCall, setStopScrollApiCall] = useState(0); //? to stop scroll api on no-content

  let getSeverityTableData = async (params = { from: initTable.from, to: initTable.to }) => {
    try {
      let paylodParams = {
        reqBody,
        from: params.from,
        to: params.to,
        tableName: 'Severity',
        tableContent: params.tableContent || tableContent,
      };

      if (paylodParams.from === 0 && paylodParams.to === 20) {
        let Response = await getDefectStatisticTableData(paylodParams);
        setTableData(Response.defects);
        setStopScrollApiCall(Response.totalDefects);
        setCallOnViewAll(false); //? to reset viewAll API call state
      } else if (paylodParams.from !== 0 && paylodParams.to !== 20 && stopScrollApiCall > paylodParams.from) {
        let Response = await getDefectStatisticTableData(paylodParams);
        setTableData([...tableData, ...Response.defects]);
        setStopScrollApiCall(Response.totalDefects);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (severityTableScrollPos) {
      setInitTable({
        from: initTable.to,
        to: initTable.to + 10,
      });
    }
  }, [severityTableScrollPos]);

  useEffect(() => {
    if (initTable.from !== 0 && initTable.to !== 20) {
      getSeverityTableData();
    }
  }, [initTable]);

  //? States for scroll APi :: END

  useEffect(() => {
    setSeries(severityChartData.series);
    setChartLabel(severityChartData.label);
    setSeriesColor(severityChartData.colorCode);
  }, [severityChartData]);

  // TODO ::NOTE:: when request body change based on side filters

  useEffect(() => {
    if (callAllApiOnFilterChange) {
      setTableContent('ALL');
      let params = {
        from: 0,
        to: 20,
        tableContent: 'ALL',
      };
      getSeverityTableData(params);
    }
  }, [callAllApiOnFilterChange]);

  // TODO :: when chick on view All legend reset initTable value & scroll position :: START
  useEffect(() => {
    if (callOnViewAll) {
      setInitTable({
        from: 0,
        to: 20,
      });

      scrollTop(severityTableRef);

      let params = {
        from: 0,
        to: 20,
      };
      getSeverityTableData(params);
    }
  }, [callOnViewAll]);

  // TODO :: when chick on view All legend reset initTable value & scroll position :: END

  let handleClickNameByIndexAndApiCall = (index) => {
    setTableContent(chartLabel.at(index));
    scrollTop(severityTableRef);

    let params = {
      from: 0,
      to: 20,
      tableContent: chartLabel.at(index),
    };
    getSeverityTableData(params);
  };

  let severityStatisticsColumn = React.useMemo(
    () => [
      {
        Header: 'BUG ID',
        accessor: 'defectId',
        width: '20%',
        Cell: ({ row, value }) => {
          return (
            <div
              className="overflow-hidden overflow-ellipsis whitespace-nowrap pr-3 rs-blue cursor-pointer"
              title={value}
              onClick={() => getBugSummary(value, 'severityBugSummary')}
            >
              {value}
            </div>
          );
        },
      },
      {
        Header: 'SUMMARY',
        accessor: 'summary',
        width: '40%',
        Cell: ({ value }) => {
          return (
            <div className="overflow-hidden overflow-ellipsis whitespace-nowrap pr-3" title={value}>
              {value}
            </div>
          );
        },
      },
      {
        Header: 'SEVERITY',
        accessor: 'content',
        width: '20%',
        Cell: ({ value }) => {
          return (
            <div className="overflow-hidden overflow-ellipsis whitespace-nowrap pr-3" title={value}>
              {value}
            </div>
          );
        },
      },
      {
        Header: 'CREATED BY',
        accessor: 'createdBy',
        width: '20%',
        Cell: ({ value }) => {
          return (
            <div className="overflow-hidden overflow-ellipsis whitespace-nowrap pr-3" title={value}>
              {value}
            </div>
          );
        },
      },
    ],
    []
  );

  let donutOptions = {
    series: [0, 0, 0, 0],
    chart: {
      type: 'donut',
      events: {
        dataPointSelection: function (event, chartContext, config) {
          setInitTable({
            from: 0,
            to: 20,
          });
          if (isEmptyValue(config.selectedDataPoints[0])) {
            //NOTE :: if chart protion selected deselect call All API (Reset)
            setTableContent('ALL');
            scrollTop(severityTableRef);

            getSeverityTableData();
          } else {
            handleClickNameByIndexAndApiCall(config.dataPointIndex);
          }
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    states: {
      hover: {
        filter: {
          type: 'none',
        },
      },
    },
    colors: seriesColor,
    labels: chartLabel,
    plotOptions: {
      bar: {
        borderRadius: 10,
      },
      pie: {
        donut: {
          labels: {
            show: true,
            value: {
              show: true,
              fontSize: '14px',
              fontFamily: chartFontFamily,
              fontWeight: 600,
            },
            total: {
              show: true,
              label: 'Total Defects',
              fontSize: '10px',
              fontFamily: chartFontFamily,
              fontWeight: 600,
              color: colors.black,
            },
          },
        },
      },
    },
    legend: {
      position: 'right',
      fontSize: 10,
      fontFamily: chartFontFamily,
      fontWeight: 500,
      formatter: (seriesName, options) => {
        return `${seriesName.split('-').at(-1).length > 7 ? seriesName.split('-').at(-1).slice(0, 7) + '..' : seriesName.split('-').at(-1)} : ${options.w.globals.series[options.seriesIndex]}`;
      },
      markers: {
        width: 11,
        height: 8,
        radius: 0,
      },
    },
    tooltip: {
      enabled: true,
      custom: defectsDonutChartCustomTooltip,
    },
  };

  return (
    <section className={cx(Styles['defectStatisticSection'])}>
      <article className={cx(Styles['leftGarpgBlock'])}>
        <div className="graphHeading ml-2 rs-blue uppercase fontPoppinsSemiboldSm pt-1">
          {defectAnalyticsLabel.label.severity}
        </div>
        <DefectsDonutChart
          series={series}
          donutOptions={donutOptions}
          setTableContent={setTableContent}
          setCallOnViewAll={setCallOnViewAll}
        />
      </article>
      <article className={cx(Styles['rightTableBlock'])}>
        <DefectsAnalyticTable
          setScrollPos={setSeverityTableScrollPos}
          ref={severityTableRef}
          header={`Defects By Severity-${tableContent}`}
          columns={severityStatisticsColumn}
          data={tableData}
          message={NO_DATA_FOUND}
          openBugSummaryModal={openSeverityBugSummaryModal}
          closeBugSummaryModal={closeBugSummaryModal}
          selectedDefectID={selectedDefectID}
        />
      </article>
    </section>
  );
};

export default SeverityStatistics;
